// The markup editor's drawing model. All geometry is stored in percentages of
// the rendered content rect (and widths/sizes as fractions of content width),
// so an item draws onto the same image/page feature at any display size — and
// scales correctly when baked into the file at natural resolution.


export type MarkupTool = 'select' | 'pen' | 'marker' | 'line' | 'arrow' | 'rect' | 'ellipse' | 'cloud' | 'text' | 'eraser';
export type MarkupColorId = 'red' | 'amber' | 'green' | 'blue' | 'black' | 'white';
export const MARKUP_COLORS: readonly MarkupColorId[] = ['red', 'amber', 'green', 'blue', 'black', 'white'];
export type MarkupColor = MarkupColorId | string; // A palette id, or a user-picked '#rrggbb' from the custom color input.

export interface IMarkupPoint {
  x: number; // % of content width
  y: number; // % of content height
}

export type MarkupItem =
  | {
      kind: 'stroke';
      tool: 'pen' | 'marker' | 'line' | 'arrow' | 'rect' | 'ellipse' | 'cloud';
      color: MarkupColor;
      width: number; // Fraction of content width (e.g. 0.004 = 0.4%)
      points: IMarkupPoint[]; // pen/marker: the polyline; the shapes: [start, end] drag corners
    }
  | {
      kind: 'text';
      color: MarkupColor;
      x: number; // Box top-left
      y: number;
      w: number; // Box width (% of content width) — text wraps inside; height follows content
      size: number; // Font size as a fraction of content width
      text: string; // May contain newlines; wraps to the box width either way
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
      align?: TextAlignOption; // Line alignment INSIDE the box; default 'left'
      direction?: TextDirection; // Bidi base direction; default 'ltr'
    }

export type TextAlignOption = 'left' | 'center' | 'right';
type TextDirection = 'ltr' | 'rtl';

/** The live text-tool settings, applied to each stamp as it is placed. */
export interface ITextStyle {
  size: number; // Fraction of content width, like MarkupItem text size
  bold: boolean;
  italic: boolean;
  underline: boolean;
  align: TextAlignOption;
  direction: TextDirection;
}

/** Committed items per page. */
export type ItemsByPage = Record<number, MarkupItem[]>;

/** The toolbar's stroke-width scale: 1–10 steps, each this fraction of the
 *  content width. Every drawing tool remembers its own width; the marker
 *  defaults wide and translucent — a highlighter over the content. */
export const STROKE_WIDTH_STEP = 0.002;
export const MIN_STROKE_WIDTH_STEPS = 1;
export const MAX_STROKE_WIDTH_STEPS = 10;
export const DEFAULT_STROKE_WIDTH_STEPS = 2;
export const DEFAULT_MARKER_WIDTH_STEPS = 7;
export const MARKER_ALPHA = 0.4;
/** Shown as "12" in the toolbar's size input (fraction × 1000). */
const DEFAULT_TEXT_SIZE = 0.012;
/** A new text box's width, % of content — resizable afterwards. */
export const DEFAULT_TEXT_BOX_WIDTH = 15;
export const MIN_TEXT_BOX_WIDTH = 8;
export const DEFAULT_TEXT_STYLE: ITextStyle = {
  size: DEFAULT_TEXT_SIZE,
  bold: false,
  italic: false,
  underline: false,
  align: 'left',
  direction: 'ltr',
};

/**
 * Resolves a markup color to its concrete CSS value at runtime. A custom
 * '#rrggbb' passes through; a palette id reads its `--markup-*` token, so
 * tokens.css stays the single source of the palette hex values (canvas and
 * PDF export cannot read `var(...)` references themselves).
 */
export function resolveMarkupColor(color: MarkupColor): string {
  if (color.startsWith('#')) return color;
  return getComputedStyle(document.documentElement).getPropertyValue(`--markup-${color}`).trim();
}

const EXPORT_MIME: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
};

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
}

/** The canvas export MIME for an image document's name ('' for PDFs). */
export function exportMimeFor(docName: string): string {
  return EXPORT_MIME[extensionOf(docName)] ?? '';
}

/**
 * Whether a version can be opened in the markup editor: published history
 * (approved or superseded) of a re-encodable image or a PDF. The result of a
 * save is always a NEW draft version through the normal upload flow.
 */
/** The freehand tools — captured as polylines; everything else is two points. */
export function isFreehandTool(tool: MarkupTool): tool is 'pen' | 'marker' {
  return tool === 'pen' || tool === 'marker';
}

/** An item's move anchor: a box's top-left, a stroke's first point. */
export function itemAnchor(item: MarkupItem): IMarkupPoint {
  return item.kind === 'stroke' ? item.points[0]! : { x: item.x, y: item.y };
}

/** The item relocated so its anchor lands on `to` — a stroke translates all
 *  of its points by the same delta. Shared by the live drag preview and the
 *  committed move, so they land on identical geometry. */
export function translateItem(item: MarkupItem, to: IMarkupPoint): MarkupItem {
  if (item.kind !== 'stroke') return { ...item, x: to.x, y: to.y };
  const anchor = item.points[0]!;
  const dx = to.x - anchor.x;
  const dy = to.y - anchor.y;
  return { ...item, points: item.points.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
}

/** The item resized so its bounding width (%) becomes `w`. Text boxes change
 *  width (the text re-wraps), strokes scale all points
 *  uniformly around their bounding box's top-left (ink width unchanged).
 *  Shared by the live resize preview and the commit. */
export function resizeItemToWidth(item: MarkupItem, w: number): MarkupItem {
  if (item.kind === 'text') return { ...item, w };
  const xs = item.points.map((p) => p.x);
  const left = Math.min(...xs);
  const top = Math.min(...item.points.map((p) => p.y));
  const boundsW = Math.max(...xs) - left;
  if (boundsW < 0.5) return item; // a (near-)vertical shape has no width to scale by
  const factor = w / boundsW;
  return { ...item, points: item.points.map((p) => ({ x: left + (p.x - left) * factor, y: top + (p.y - top) * factor })) };
}